import React, { useEffect, useState } from 'react';
import type { Ctx } from '../ctx';
import { ErrorBanner, Field, Modal } from '../ui';

/**
 * Profilo dell'azienda per il piano (29/10/2026, «un solo programma»): le stesse domande del Piano del programma interno
 * (app/Ui/PlanPage.cs), ora nel CRM. Si salva nel fascicolo (PUT /plan/profile) e «Crea dai PC collegati» lo usa.
 * Le voci che il modulo non mostra (es. documenti_pronti, software_su_misura) restano come erano.
 */

type Profile = Record<string, unknown>;
type Yn = '' | 'si' | 'no';

const str = (p: Profile, k: string) => (typeof p[k] === 'string' ? (p[k] as string) : typeof p[k] === 'number' ? String(p[k]) : '');
const num = (p: Profile, k: string) => (typeof p[k] === 'number' && p[k] !== 0 ? String(p[k]).replace('.', ',') : typeof p[k] === 'string' ? (p[k] as string) : '');
const toNum = (t: string) => {
  const n = Number.parseFloat(t.trim().replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
};

function Choice<T extends string>({ value, options, onChange }: { value: T; options: [T, string][]; onChange: (v: T) => void }) {
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      {options.map(([v, label]) => (
        <button key={v || 'vuoto'} type="button" className={`btn small ${value === v ? 'dark' : 'ghost'}`} onClick={() => onChange(v)}>
          {label}
        </button>
      ))}
    </div>
  );
}

const YN: [Yn, string][] = [['', 'Non so'], ['si', 'Sì'], ['no', 'No']];
const yn = (p: Profile, k: string): Yn => (p[k] === 'si' ? 'si' : p[k] === 'no' ? 'no' : '');

const TEXTS: { key: string; label: string; hint?: string }[] = [
  { key: 'partita_iva', label: 'Partita IVA' },
  { key: 'codice_fiscale', label: 'Codice fiscale' },
  { key: 'indirizzo', label: 'Indirizzo' },
  { key: 'pec', label: 'PEC' },
];
const SIZES: { key: string; label: string; hint: string }[] = [
  { key: 'dipendenti', label: 'Dipendenti', hint: 'Quante persone lavorano nell’azienda.' },
  { key: 'fatturato_milioni', label: 'Fatturato (milioni di €)', hint: 'Dell’ultimo bilancio. Esempio: 28,7' },
  { key: 'bilancio_milioni', label: 'Totale di bilancio (milioni di €)', hint: 'Dell’ultimo bilancio. Se non lo sai, lascia vuoto.' },
  { key: 'pc', label: 'Computer', hint: 'In tutto, anche quelli non controllati. Vuoto = quelli dei report.' },
  { key: 'server', label: 'Server', hint: 'Vuoto = quelli dei report.' },
  { key: 'utenti', label: 'Utenti', hint: 'Quante persone usano i computer. Vuoto = come i computer.' },
];
const HAVE: { key: string; label: string; hint: string }[] = [
  { key: 'edr', label: 'Protezione avanzata (EDR)', hint: 'Per esempio CrowdStrike. Vuoto = non ce l’hanno.' },
  { key: 'firewall', label: 'Firewall con licenza attiva', hint: 'Il modello. Vuoto = non ce l’hanno o non si sa.' },
  { key: 'backup', label: 'Copie di sicurezza', hint: 'Il programma. Vuoto = nessuno.' },
  { key: 'siem', label: 'Raccolta dei registri (SIEM)', hint: 'Per esempio Wazuh. Vuoto = nessuno.' },
  { key: 'posta', label: 'Posta', hint: 'Per esempio Microsoft 365. Vuoto = lo leggo dal dominio.' },
  { key: 'password_manager', label: 'Gestore di password', hint: 'Per esempio Bitwarden. Vuoto = nessuno.' },
];
const ASKS: { key: string; label: string; hint: string }[] = [
  { key: 'rete_separata', label: 'Rete divisa in zone', hint: 'Uffici, produzione, ospiti separati.' },
  { key: 'copia_fuori_sede', label: 'Copia fuori dall’azienda', hint: 'Le copie sono anche in un altro posto?' },
  { key: 'mfa', label: 'Codice sul telefono (2FA)', hint: 'Per posta e accesso da fuori.' },
  { key: 'permessi_cartelle', label: 'Cartelle divise per reparto', hint: 'Sul server o NAS ognuno vede solo le sue.' },
  { key: 'usb_controllate', label: 'Chiavette USB bloccate', hint: 'Funzionano solo quelle autorizzate.' },
  { key: 'blocco_cloud_ia', label: 'Cloud personali e IA bloccati', hint: 'WeTransfer, Dropbox privati, IA non approvate.' },
  { key: 'isolamento_auto', label: 'Isolamento automatico', hint: 'L’EDR stacca dalla rete il PC negli allarmi gravi.' },
  { key: 'file_esca', label: 'File esca', hint: 'File civetta che fanno scattare un avviso.' },
];

export function SecurityProfileModal({ ctx, siteId, siteName, onClose, onSaved }: {
  ctx: Ctx; siteId: number; siteName: string; onClose: () => void; onSaved: (cliente: string) => void;
}) {
  const [p, setP] = useState<Profile | null>(null);
  const [sectors, setSectors] = useState<{ id: string; nome: string }[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let off = false;
    void (async () => {
      try {
        const [saved, list] = await Promise.all([
          ctx.api.get<{ profile: Profile } | null>(`/api/guard/sites/${siteId}/plan/profile`),
          ctx.api.get<{ sectors: { id: string; nome: string }[] }>('/api/guard/plan/sectors').catch(() => ({ sectors: [] })),
        ]);
        if (off) return;
        setSectors(list.sectors);
        setP(saved?.profile ?? { cliente: siteName, nis2: 'auto', voucher: true, dati_sanitari: 'no', settore: list.sectors[0]?.id ?? '' });
      } catch (e) {
        if (!off) setError((e as Error).message);
      }
    })();
    return () => { off = true; };
  }, [ctx.api, siteId, siteName]);

  const set = (k: string, v: unknown) => setP((cur) => ({ ...(cur ?? {}), [k]: v }));
  const have = (p?.gia_presenti && typeof p.gia_presenti === 'object' ? p.gia_presenti : {}) as Profile;
  const setHave = (k: string, v: string) => set('gia_presenti', { ...have, [k]: v });

  const save = async () => {
    if (!p) return;
    const cliente = str(p, 'cliente').trim();
    if (cliente.length < 2) { setError('Scrivi il nome dell’azienda.'); return; }
    const out: Profile = { ...p, cliente };
    for (const s of SIZES) out[s.key] = toNum(str(p, s.key));
    for (const t of TEXTS) out[t.key] = str(p, t.key).trim();
    out.tecnico_cliente = str(p, 'tecnico_cliente').trim();
    out.gia_presenti = Object.fromEntries(HAVE.map((h) => [h.key, str(have, h.key).trim()]));
    setBusy(true);
    setError(null);
    try {
      const r = await ctx.api.request<{ profile: { cliente: string } }>('PUT', `/api/guard/sites/${siteId}/plan/profile`, out);
      onSaved(r.profile.cliente);
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };

  const input = (k: string, obj: Profile = p ?? {}, onChange = (v: string) => set(k, v)) => (
    <input value={str(obj, k)} onChange={(e) => onChange(e.target.value)} />
  );
  const section = (title: string) => <h4 style={{ margin: '18px 0 8px' }}>{title}</h4>;

  return (
    <Modal
      title={`Profilo di ${siteName}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn ghost" onClick={onClose}>Annulla</button>
          <button type="button" className="btn dark" disabled={busy || !p} onClick={() => void save()}>{busy ? 'Salvo…' : 'Salva il profilo'}</button>
        </>
      }
    >
      <ErrorBanner message={error} onClose={() => setError(null)} />
      {!p ? <div className="muted small">Caricamento…</div> : (
        <div>
          <p className="muted small" style={{ marginTop: 0 }}>Le stesse domande del Piano: servono a creare il piano dai PC collegati.</p>
          {section('Chi è il cliente')}
          <Field label="Nome dell’azienda" hint="Come va scritto sul piano.">{input('cliente')}</Field>
          {TEXTS.map((t) => <Field key={t.key} label={t.label}>{input(t.key)}</Field>)}

          {section('Quanto è grande')}
          <Field label="Settore" hint="Serve a capire se vale la NIS2. Se non c’è il tuo, scegli «Altro».">
            <select value={str(p, 'settore')} onChange={(e) => set('settore', e.target.value)}>
              {!sectors.some((s) => s.id === str(p, 'settore')) && <option value={str(p, 'settore')}>{str(p, 'settore') || '—'}</option>}
              {sectors.map((s) => <option key={s.id} value={s.id}>{s.nome}</option>)}
            </select>
          </Field>
          {SIZES.map((s) => (
            <Field key={s.key} label={s.label} hint={s.hint}>
              <input inputMode="decimal" value={num(p, s.key)} onChange={(e) => set(s.key, e.target.value)} />
            </Field>
          ))}

          {section('NIS2 e voucher')}
          <Field label="La NIS2 vale per loro?" hint="«Calcola da solo» usa settore, dipendenti e fatturato.">
            <Choice value={p.nis2 === 'si' ? 'si' : p.nis2 === 'no' ? 'no' : 'auto'} options={[['auto', 'Calcola da solo'], ['si', 'Sì, vale'], ['no', 'No, non vale']]} onChange={(v) => set('nis2', v)} />
          </Field>
          <Field label="Registrati sul portale ACN?" hint="Se sono già registrati, non lo chiediamo più.">
            <Choice value={yn(p, 'acn_registrato')} options={YN} onChange={(v) => set('acn_registrato', v)} />
          </Field>
          <Field label="Forniscono ad aziende NIS2?" hint="Devono dimostrare la sicurezza ai clienti soggetti.">
            <Choice value={yn(p, 'fornisce_a_soggetti_nis2')} options={YN} onChange={(v) => set('fornisce_a_soggetti_nis2', v)} />
          </Field>
          <Field label="Trattano dati sulla salute?" hint="Cliniche, studi medici, laboratori.">
            <Choice value={p.dati_sanitari === 'si' ? 'si' : 'no'} options={[['no', 'No'], ['si', 'Sì']]} onChange={(v) => set('dati_sanitari', v)} />
          </Field>
          <label className="small" style={{ display: 'flex', gap: 8, alignItems: 'center', margin: '6px 0 4px' }}>
            <input type="checkbox" checked={p.voucher !== false} onChange={(e) => set('voucher', e.target.checked)} />
            Il cliente fa il voucher (c’è il tetto del 30% sui servizi)
          </label>

          {section('Cosa hanno già')}
          {HAVE.map((h) => <Field key={h.key} label={h.label} hint={h.hint}>{input(h.key, have, (v) => setHave(h.key, v))}</Field>)}
          {ASKS.map((a) => (
            <Field key={a.key} label={a.label} hint={a.hint}>
              <Choice value={yn(p, a.key)} options={YN} onChange={(v) => set(a.key, v)} />
            </Field>
          ))}
          <Field label="Tecnico del cliente" hint="Il nome, se c’è un tecnico che lavora per il cliente.">{input('tecnico_cliente')}</Field>
        </div>
      )}
    </Modal>
  );
}
